import { http } from '@/lib/http';
import { stayWindow, type StayNightsResult } from './stay';
import type {
  AllocationState,
  HotelOption,
  HotelSearchRequest,
  IPolicyViolation,
} from './types';
import type { SubCase } from '@/lib/sub-cases/types';

function allocPath(caseUrn: string): string {
  return `v1/cases/${encodeURIComponent(caseUrn)}/allocation`;
}

export async function getAllocation(caseUrn: string): Promise<AllocationState> {
  return http.get(allocPath(caseUrn)).json<AllocationState>();
}

/** Existing case read, shared with the passenger list's query cache. */
export async function getHotelSearchGroups(caseUrn: string): Promise<SubCase[]> {
  return http.get(`v1/cases/${encodeURIComponent(caseUrn)}/sub-cases`).json<SubCase[]>();
}

// The operator-bff hotels proxy (E-2) speaks the booking domain's search shape:
//   req  { airportUrn, checkInUtc, checkOutUtc, guests, airlineCode? }
//   res  { offers: BookingHotelOffer[], count, correlationUrn, policyUrn, policyMatchType }
// The UI works in operator-friendly terms ({ airportCode, guests }), so
// this adapter derives the booking request and maps offers → HotelOption[].
interface BookingHotelOffer {
  offerUrn: string;
  hotelUrn: string;
  // details/pricing are optional defensively — sparse upstream adapters may omit them.
  details?: {
    name?: string;
    stars?: number;
    distanceKm?: number;
    geo?: { lat: number; lon: number };
    amenities?: string[];
  };
  /** The airline's PRICE (the BFF prices booking's offer, commercial-contract §6.13);
   *  `null` when the price card was unavailable. */
  pricing?: { currency?: string; perNight?: number | null; total?: number | null };
  capacity?: number;
  source?: 'gds' | 'contract';
  /** The vendor's free-cancellation cutoff (ISO), forwarded verbatim by the BFF. */
  cancellationDeadline?: string;
}
interface BookingHotelSearchResponse {
  offers: BookingHotelOffer[];
  count: number;
  correlationUrn: string;
  policyUrn: string | null;
  policyMatchType: string | null;
  /** Only when asked (`includeOutOfPolicy`) AND the viewer holds `hotels.policy.override`. */
  outOfPolicy?: Array<BookingHotelOffer & { violations?: IPolicyViolation[] }>;
  /** `false` → the BFF could not read the price card; every offer amount is `null`. */
  priced?: boolean;
}

function optionOf(offer: BookingHotelOffer, policyCompliant: boolean): HotelOption {
  return {
    offerUrn: offer.offerUrn,
    hotelUrn: offer.hotelUrn,
    name: offer.details?.name ?? hotelNameFromUrn(offer.hotelUrn),
    stars: offer.details?.stars ?? 0,
    distanceKm: offer.details?.distanceKm ?? 0,
    availableRooms: offer.capacity ?? 0,
    // The airline's price per night; unknown stays unknown (null → «No disponible»),
    // never a 0 that would read as a free room.
    ratePerRoomUSD: offer.pricing?.perNight ?? offer.pricing?.total ?? null,
    policyCompliant,
    ...(offer.details?.geo ? { geo: offer.details.geo } : {}),
    ...(offer.details?.amenities ? { amenities: offer.details.amenities } : {}),
  };
}

export async function searchHotels(request: HotelSearchRequest): Promise<HotelOption[]> {
  // The stay the caller is actually booking (feature `stay-nights`, N8). This used
  // to be a hardcoded `now → now + 24h` right here — one of three such literals in
  // the console — so a passenger stranded three days was quoted, ranked and
  // capacity-checked for ONE night. The window now comes from the group's
  // `stayPlan`; `stayWindow` still yields the identical 1-night default when there
  // is no plan, so a group with no evidence searches exactly as it did before.
  const stay = request.stay ?? stayWindow(null);
  const res = await http
    .post('v1/hotels/search', {
      json: {
        airportUrn: `urn:airport:${request.airportCode}`,
        airportCode: request.airportCode,
        checkInUtc: stay.checkInUtc,
        checkOutUtc: stay.checkOutUtc,
        guests: Math.max(1, request.guests),
        ...(request.includeOutOfPolicy ? { includeOutOfPolicy: true } : {}),
      },
    })
    .json<BookingHotelSearchResponse>();

  // The booking domain already filters/ranks by policy, so returned offers are
  // policy-compliant. `correlationUrn`/`policyUrn` are available for later audit.
  // Map defensively (E-2d): some upstream adapters return sparse offers without
  // a full `details`/`pricing` block — fall back so hotel cards never render
  // blank (a name derived from the hotel URN, price 0) instead of crashing or
  // showing nulls.
  // The offers the policy rules out (asked for, and allowed): after the compliant ones,
  // each saying which rules it breaks — a person chooses one only with a reason.
  //
  // Each option carries the stay it was quoted for (`searchedStay`): the hold and the book
  // reuse it verbatim, because booking prices from the quote parked for exactly this
  // offer + stay (commercial-contract §6.15).
  const searchedStay = {
    checkInUtc: stay.checkInUtc,
    checkOutUtc: stay.checkOutUtc,
    ...(typeof stay.nights === 'number' ? { nights: stay.nights } : {}),
  };
  return [
    ...(res.offers ?? []).map((offer) => ({ ...optionOf(offer, true), searchedStay })),
    ...(res.outOfPolicy ?? []).map((offer) => ({
      ...optionOf(offer, false),
      violations: offer.violations ?? [],
      searchedStay,
    })),
  ];
}

/** Readable label from a hotel URN (`urn:hotel:scl-001:vendor:gds` → "scl-001"). */
function hotelNameFromUrn(hotelUrn: string): string {
  const parts = hotelUrn.split(':');
  const idIdx = parts.indexOf('hotel') + 1;
  return idIdx > 0 && parts[idIdx] ? parts[idIdx] : 'Hotel';
}

export async function approveDemand(caseUrn: string, wave: number): Promise<AllocationState> {
  const res = await http
    .post(`${allocPath(caseUrn)}/approve-demand`, { json: { wave } })
    .json<AllocationState>();
  return res;
}

// Hotel reserve (hold→book) + cancel moved to the booking lifecycle client
// (lib/booking/api.ts) per the frozen contract v1. allocation/api keeps
// allocation summary, demand approval, hotel search, and relaxation.

export interface RelaxationRequest {
  caseUrn: string;
  axis: 'cost' | 'distance';
  amount: number;
  reason: string;
}

export async function requestRelaxation(payload: RelaxationRequest): Promise<void> {
  await http.post('v1/allocation/override-request', { json: payload });
}

// c7 — manual hotel override (TDS §8). The operator force-assigns a specific
// hotel to a sub-case (PNR group) outside the auto-allocation ranking, with a
// justification that lands in the audit trail. POST /v1/allocation/override.
export interface OverrideRequest {
  caseUrn: string;
  subCaseUrn: string;
  hotelUrn: string;
  offerUrn?: string;
  rooms: number;
  reason: string;
}

export async function overrideAllocation(payload: OverrideRequest): Promise<void> {
  await http.post('v1/allocation/override', { json: payload });
}

/* ── the operator's NIGHTS control (feature `stay-nights`, N8 → N7) ─────────── */

/**
 * `operator-bff:POST /v1/disruptions/{urn}/stay-nights@v1` — the blanket number
 * for a whole disruption ("everyone here is stranded three nights").
 *
 * The BFF proxies it to the cases seam that owns the §7 ladder. Groups a human
 * already pinned per-PNR (`OPERATOR_GROUP`) KEEP their own number — the response's
 * `overridden` says how many, which is the same figure the control previews before
 * the operator confirms. A `StayPlanChanged` rides out to every operator on the
 * event, so nobody has to refresh.
 *
 * No idempotency key: this is a last-write-wins setting, not a money movement, and
 * a retried set of the same number is the same state.
 */
export async function setDisruptionStayNights(
  disruptionUrn: string,
  nights: number,
): Promise<StayNightsResult> {
  return http
    .post(`v1/disruptions/${encodeURIComponent(disruptionUrn)}/stay-nights`, { json: { nights } })
    .json<StayNightsResult>();
}

/**
 * Where a per-PNR nights write is addressed. A PNR inside a disruption writes
 * through the disruption route (the proven N8 wire); a PNR on a STANDALONE case
 * — a care-only case opened by hand, say — has no disruption to route through,
 * so the BFF exposes a case-scoped twin with the identical body/response. The
 * caller states which one it has; `caseUrn` is always known on the group, so
 * every group can be written to.
 */
export type StayNightsScope = { disruptionUrn: string } | { caseUrn: string };

/**
 * `operator-bff:POST /v1/disruptions/{urn}/pnrs/{sub}/stay-nights@v1` (or its
 * case-scoped twin `POST /v1/cases/{urn}/pnrs/{sub}/stay-nights@v1`) — the
 * per-PNR override, the TOP rung of the ladder.
 *
 * `nights: null` CLEARS it and drops the group back onto the disruption default /
 * the derived evidence — it does not pin whatever was last derived. That is why
 * the parameter is `number | null` and not optional: `undefined` would be a
 * malformed body, and "clear" must be something the operator states.
 */
export async function setGroupStayNights(
  scope: StayNightsScope,
  subCaseUrn: string,
  nights: number | null,
): Promise<StayNightsResult> {
  const base =
    'disruptionUrn' in scope
      ? `v1/disruptions/${encodeURIComponent(scope.disruptionUrn)}`
      : `v1/cases/${encodeURIComponent(scope.caseUrn)}`;
  return http
    .post(`${base}/pnrs/${encodeURIComponent(subCaseUrn)}/stay-nights`, { json: { nights } })
    .json<StayNightsResult>();
}

/* ── the STAY-HOME declaration (care-only round 3) ──────────────────────────── */

/** 200 body of both stay-home routes (mirror of the cases seam). */
export interface StayHomeResult {
  subCaseUrn: string;
  stayHome: boolean;
}

/**
 * `operator-bff:POST /v1/disruptions/{urn}/pnrs/{sub}/stay-home@v1` (or its
 * case-scoped twin `POST /v1/cases/{urn}/pnrs/{sub}/stay-home@v1`) — the
 * operator declares this PNR's passenger STAYS AT THEIR OWN HOME (disrupted at
 * their home base): no hotel demand, rebooking untouched. Scope-aware exactly
 * like `setGroupStayNights`; ANY case type, not just care-only.
 *
 * cases stamps the sub-case rebooking with `passengerElection: 'LOCAL_RESIDENT'`,
 * `requiresHotel: false`, `hotelRequirementReason: 'OVERRIDDEN'` — the explicit
 * suppression every needs-hotel gate honours at any status. Idempotent;
 * undoable via `revertStayHome`.
 */
export async function declareStayHome(
  scope: StayNightsScope,
  subCaseUrn: string,
  input?: StayHomeInput,
): Promise<StayHomeResult> {
  const base =
    'disruptionUrn' in scope
      ? `v1/disruptions/${encodeURIComponent(scope.disruptionUrn)}`
      : `v1/cases/${encodeURIComponent(scope.caseUrn)}`;
  const note = input?.note?.trim();
  const homeAddress = input?.homeAddress?.trim();
  return http
    .post(`${base}/pnrs/${encodeURIComponent(subCaseUrn)}/stay-home`, {
      json: { ...(note ? { note } : {}), ...(homeAddress ? { homeAddress } : {}) },
    })
    .json<StayHomeResult>();
}

/**
 * What the stay-home DECLARE may carry (`stay-home@v1`, both optional). The
 * address is PII: it goes up the wire once, cases geocodes it into the
 * roster's `homeDestination` (point + coarse label), and the street itself is
 * never read back by this app.
 */
export interface StayHomeInput {
  note?: string;
  homeAddress?: string;
}

/**
 * `DELETE …/pnrs/{sub}/stay-home` — the idempotent undo. cases reverts ONLY a
 * `LOCAL_RESIDENT` election (back to undefined, so the gates fall back to
 * status behaviour and a later flight selection recomputes the requirement);
 * answering `stayHome: false` also when there was nothing to revert.
 */
export async function revertStayHome(
  scope: StayNightsScope,
  subCaseUrn: string,
): Promise<StayHomeResult> {
  const base =
    'disruptionUrn' in scope
      ? `v1/disruptions/${encodeURIComponent(scope.disruptionUrn)}`
      : `v1/cases/${encodeURIComponent(scope.caseUrn)}`;
  return http
    .delete(`${base}/pnrs/${encodeURIComponent(subCaseUrn)}/stay-home`)
    .json<StayHomeResult>();
}
